import { Box } from "@mui/material";
import {
  WARNING_SEVERITIES,
  WARNING_CATEGORY_LABELS,
  type WarningCategory,
} from "@caredna-marlin/utils/warnings";
import { FilterChip } from "~/components/common/forms/FilterChip";

export type Options = Map<string, string>;

export type WarningFilterValues = {
  visitTypeIds: string[];
  patientIds: string[];
  providerIds: string[];
  serviceLineIds: string[];
  warningCategories: WarningCategory[];
};

export const HIGH_SEVERITY_NAME = "Critical issues";
export const MED_SEVERITY_NAME = "Warnings";
export const LOW_SEVERITY_NAME = "Info";

export const MED_TO_HIGH_SEVERITY_LABEL = `${MED_SEVERITY_NAME} and ${HIGH_SEVERITY_NAME.toLowerCase()}`;
export const ANY_SEVERITY_LABEL = "All issues";

// Default: all categories with severity >= 2
export const DEFAULT_WARNING_CATEGORIES: WarningCategory[] = (Object.keys(
  WARNING_SEVERITIES
) as WarningCategory[]).filter((k) => WARNING_SEVERITIES[k] >= 2);

// helpers
export const arraysEqualAsSets = <T,>(a: T[], b: T[]) => {
  if (a.length !== b.length) return false;
  const s = new Set(a as any[]);
  for (const x of b as any[]) if (!s.has(x)) return false;
  return true;
};

const categoriesToLabel = (cats: WarningCategory[]) => {
  if (cats.length == 0) return "All issues"
  return cats.map((c) => WARNING_CATEGORY_LABELS[c]).join(", ");
}

type Props = {
  values: WarningFilterValues;
  setValues: (v: WarningFilterValues) => void;
  onOpenDialog: () => void;

  visitTypeOptions: Options;
  patientOptions: Options;
  providerOptions: Options;
  serviceLineOptions: Options;
};

export default function WarningFilterChips({
  values,
  setValues,
  onOpenDialog,
  visitTypeOptions,
  patientOptions,
  providerOptions,
  serviceLineOptions,
}: Props) {
  const vtLabel =
    values.visitTypeIds.map((id) => visitTypeOptions.get(id) ?? "").filter(Boolean).join(", ") ||
    "";

  const patientLabel =
    values.patientIds.map((id) => patientOptions.get(id) ?? "").filter(Boolean).join(", ") || "";

  const providerLabel =
    values.providerIds.map((id) => providerOptions.get(id) ?? "").filter(Boolean).join(", ") || "";

  const serviceLineLabel =
    values.serviceLineIds.map((id) => serviceLineOptions.get(id) ?? "").filter(Boolean).join(", ") || "";

  const isDefaultCategories = arraysEqualAsSets(values.warningCategories, DEFAULT_WARNING_CATEGORIES);

  // Display rule: list category labels unless it matches default (then use the old summary label)
  const categoriesLabel = isDefaultCategories
    ? MED_TO_HIGH_SEVERITY_LABEL
    : categoriesToLabel(values.warningCategories);

  return (
    <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
      {/* Categories chip */}
      <FilterChip
        selected={!isDefaultCategories}
        label={categoriesLabel}
        emptyLabel={MED_TO_HIGH_SEVERITY_LABEL}
        onClick={onOpenDialog}
        onDelete={() =>
          setValues({
            ...values,
            warningCategories: DEFAULT_WARNING_CATEGORIES,
          })
        }
      />

      <FilterChip
        selected={values.visitTypeIds.length > 0}
        label={vtLabel}
        emptyLabel="Any visit type"
        onClick={onOpenDialog}
        onDelete={() => setValues({ ...values, visitTypeIds: [] })}
      />
      <FilterChip
        selected={values.patientIds.length > 0}
        label={patientLabel}
        emptyLabel="Any patient"
        onClick={onOpenDialog}
        onDelete={() => setValues({ ...values, patientIds: [] })}
      />
      <FilterChip
        selected={values.providerIds.length > 0}
        label={providerLabel}
        emptyLabel="Any provider"
        onClick={onOpenDialog}
        onDelete={() => setValues({ ...values, providerIds: [] })}
      />
      <FilterChip
        selected={values.serviceLineIds.length > 0}
        label={serviceLineLabel}
        emptyLabel="Any service line"
        onClick={onOpenDialog}
        onDelete={() => setValues({ ...values, serviceLineIds: [] })}
      />
    </Box>
  );
}
